import { usePageSEO } from "@/lib/use-page-seo";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "@/lib/use-locale-path";
import WCLogo from "@/components/WCLogo";
import HowItWorks from "@/components/HowItWorks";
import FAQSection from "@/components/FAQSection";
import MegaFooter from "@/components/MegaFooter";
import ObfuscatedEmail from "@/components/ObfuscatedEmail";

const SERIES = [
  {
    name: "Formula 1",
    slug: "f1",
    description: "The premier class of single-seater racing. 24 race weekends per season covering practice, qualifying, sprint and race sessions.",
    tip: "Select Practice, Qualifying and Race to get the full weekend. Sprint sessions are available at select rounds.",
  },
  {
    name: "Formula 2",
    slug: "f2",
    description: "The main feeder series to F1, racing at F1 weekends. Includes practice, qualifying, sprint race and feature race.",
    tip: "F2 races at most F1 European rounds and select flyaways — a great complement to your F1 calendar.",
  },
  {
    name: "Formula 3",
    slug: "f3",
    description: "Junior open-wheel racing at select F1 weekends. Practice, qualifying, sprint and feature race.",
    tip: "F3 runs at a subset of F1 rounds. Add it alongside F1 to follow the next generation of talent.",
  },
  {
    name: "Formula E",
    slug: "fe",
    description: "All-electric single-seater racing on city-street circuits worldwide. Practice, qualifying and E-Prix.",
    tip: "Formula E runs on its own calendar, separate from F1 weekends — ideal for filling gaps between grands prix.",
  },
  {
    name: "IndyCar",
    slug: "indycar",
    description: "American open-wheel racing including the Indianapolis 500, on ovals, road courses and street circuits. Practice, qualifying and race.",
    tip: "IndyCar overlaps with the F1 calendar on some weekends, making a combined feed especially useful.",
  },
];

export default function GuidePage() {
  usePageSEO("meta.guide_title", "/guide");
  const { t } = useTranslation();
  const lp = useLocalePath();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/40 bg-background/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <WCLogo />
          <div className="flex items-center gap-4">
            <a href={lp("/")} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              ← Back
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Page header */}
        <div className="mb-12">
          <h1
            className="text-4xl sm:text-5xl font-bold uppercase tracking-tight mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            How to Add Motorsport Sessions to Your Calendar
          </h1>
          <p className="text-muted-foreground text-base max-w-2xl leading-relaxed">
            Formula Calendar gives you a live, auto-updating ICS subscription for Formula 1, F2, F3, Formula E and IndyCar — all in one feed, personalised to the series you actually follow. Here's how it works and what each series covers.
          </p>
        </div>

        {/* What makes this different */}
        <section className="mb-12 p-6 rounded-xl border border-border/50 bg-card/40">
          <h2
            className="text-2xl font-bold uppercase tracking-tight mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Live subscription vs. static download
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm text-muted-foreground">
            <div>
              <p className="font-semibold text-foreground mb-2">Static .ics download (the old way)</p>
              <ul className="space-y-1">
                <li>• Snapshot in time — goes stale immediately</li>
                <li>• Session times change? You won't know</li>
                <li>• You have to re-download for every update</li>
                <li>• No results, no rescheduling, no cancellations</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-foreground mb-2">Formula Calendar live subscription</p>
              <ul className="space-y-1">
                <li>• Your calendar app polls the feed automatically</li>
                <li>• Session time changes push to your phone</li>
                <li>• Race results appear in your calendar entry</li>
                <li>• Set up once. Works for the whole season.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Series breakdown */}
        <section className="mb-12">
          <h2
            className="text-3xl font-bold uppercase tracking-tight mb-6"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Series guide
          </h2>
          <div className="space-y-4">
            {SERIES.map((series) => (
              <div
                key={series.slug}
                className="p-5 rounded-xl border border-border/50 bg-card/40"
              >
                <h3
                  className="text-xl font-bold uppercase mb-1"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {series.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-2">{series.description}</p>
                <p className="text-xs text-primary/80 border-l-2 border-primary/30 pl-3">{series.tip}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Step-by-step for each app */}
        <section className="mb-12">
          <h2
            className="text-3xl font-bold uppercase tracking-tight mb-6"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Adding to your calendar app
          </h2>
          <div className="space-y-6">
            {[
              {
                app: "Apple Calendar (iPhone, iPad, Mac)",
                steps: [
                  "Go to formulacal.com and pick your series.",
                  "Select the session types you want (Practice, Qualifying, Sprint, Race).",
                  "Enter your email and tap Get Calendar.",
                  "Tap the subscription link in the email — iOS will ask 'Subscribe to Calendar?'",
                  "Tap Subscribe. Done. Sessions appear immediately.",
                ],
              },
              {
                app: "Google Calendar",
                steps: [
                  "Go to formulacal.com, pick your series, and get your ICS subscription URL.",
                  "Open Google Calendar on desktop (calendar.google.com).",
                  "Click the + next to 'Other calendars' → 'From URL'.",
                  "Paste your ICS URL and click 'Add calendar'.",
                  "Google will refresh the feed on its own schedule (typically every 6–24 hours).",
                ],
              },
              {
                app: "Microsoft Outlook",
                steps: [
                  "Get your ICS subscription URL from formulacal.com.",
                  "In Outlook, go to Calendar view.",
                  "On Windows: Home tab → Open Calendar → From Internet. On Mac: File → New Calendar Subscription.",
                  "Paste your ICS URL and confirm.",
                ],
              },
            ].map(({ app, steps }) => (
              <div key={app} className="rounded-xl border border-border/50 bg-card/40 p-5">
                <h3
                  className="text-lg font-bold uppercase mb-3"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {app}
                </h3>
                <ol className="space-y-2">
                  {steps.map((step, i) => (
                    <li key={i} className="flex gap-3 text-sm text-muted-foreground">
                      <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-xs font-bold text-primary">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mb-12 p-6 rounded-xl border border-primary/20 bg-primary/5 text-center">
          <h2
            className="text-2xl font-bold uppercase tracking-tight mb-2"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Ready to subscribe?
          </h2>
          <p className="text-sm text-muted-foreground mb-4">Your first series is free. No credit card required.</p>
          <a
            href={lp("/")}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity text-sm"
          >
            Pick your series →
          </a>
        </section>

        {/* Reuse existing HowItWorks and FAQSection */}
        <HowItWorks />
        <FAQSection />
      </main>

      <MegaFooter />

      <footer className="border-t border-border/40 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            <WCLogo compact />
            <span>© {new Date().getFullYear()} Skella &amp; Co</span>
          </div>
          <div className="flex items-center gap-4">
            <a href={lp("/faq")} className="hover:text-foreground transition-colors">FAQ & Privacy</a>
            <ObfuscatedEmail />
          </div>
        </div>
      </footer>
    </div>
  );
}
